import Avatar from 'react-avatar'
import dayjs from 'dayjs'
import { DotsThree } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { COLOR_CONTRACT_BLACK } from 'constant/colors'
import { PERMISSION_BY_ROLE } from 'Utils/permissionByRol'
import { formatAmount, getRandomDarkThemeColor } from 'Utils/helperFunctions'
import { PROFILE_DETAILS_ROUTER } from 'constant/router'
import RateCell from './RateCell'

const clampPercent = (value, total) => {
  if (!total || total <= 0) return 0
  return Math.min(100, Math.max(0, (value / total) * 100))
}

const TableUserTeamRow = ({ user, billing, budget, userContext, setVisibleBudgetModal, onDelete, onSaveRate }) => {
  const { primary, secondary } = getRandomDarkThemeColor()

  // The user's current default rate (fixed or hourly based). Shown when this
  // project has no per-member override saved.
  const defaultCostRate = user?.cost_rates?.[0]

  const showMemberBudget = budget?.type === 'Fee per team member' || budget?.type === 'Hours per team member'
  const chargePerMember = billing?.method === 'Charge per team member'

  const budgetUnit = budget?.type === 'Fee per team member' ? 'money' : 'hours'
  const memberBudget = user.project_budget ?? null
  const memberRate =
    user.project_billable_rate ?? user.project_cost_rate ?? defaultCostRate?.cost_rate ?? 0
  const memberSpent =
    budgetUnit === 'money' ? (user.project_spent ?? 0) * memberRate : (user.project_spent ?? 0)
  const memberRemaining = memberBudget != null ? memberBudget - memberSpent : null

  const renderCostHint = ({ persisted }) => {
    if (persisted != null) return 'Custom rate — not the default'
    if (defaultCostRate) return `Default · ${defaultCostRate.is_hourly_based ? 'hourly' : 'fixed'} rate`
    return null
  }

  const renderBudgetHint = () => {
    if (memberBudget == null) return null
    return `Spent ${formatAmount({ value: memberSpent, unit: budgetUnit })} · ${formatAmount({ value: memberRemaining, unit: budgetUnit })} left`
  }

  return (
    <tr>
      <td>
        <Avatar
          name={`${user.firstname} ${user.lastname}`}
          className="avatarImage"
          src={user?.logo || null}
          color={primary}
          fgColor={secondary}
          round={true}
          size="38px"
          textSizeRatio={3}
        />
      </td>
      <td>
        <p className="m-0">
          {user.firstname} {user.lastname} / {user.position}
        </p>
        <span className="form-text">{user.email}</span>
        <br />
        <span className="form-text">Date added: {dayjs(user.last_activity).format('MMM DD, YYYY')}</span>
      </td>

      {showMemberBudget && (
        <td className="text-no-wrap">
          <RateCell
            id={`memberBudget-${user.id}`}
            ariaLabel={`Budget for ${user.firstname} ${user.lastname}`}
            savedRate={user.project_budget}
            unit={budgetUnit}
            onSave={(value) => onSaveRate({ userId: user.id, field: 'budget', value })}
            renderHint={renderBudgetHint}
          />
          {memberBudget != null && memberBudget > 0 && (
            <div
              style={{ width: '160px', height: '4px', marginTop: 6 }}
              className="progress"
              role="progressbar"
              aria-label="Budget used"
              aria-valuenow={clampPercent(memberSpent, memberBudget)}
              aria-valuemin="0"
              aria-valuemax="100">
              <div
                className="progress-bar"
                style={{ width: `${clampPercent(memberSpent, memberBudget)}%`, backgroundColor: '#016A67' }}
              ></div>
              {memberSpent > memberBudget && (
                <div
                  className="progress-bar"
                  style={{
                    width: `${clampPercent(memberSpent - memberBudget, memberBudget)}%`,
                    backgroundColor: '#BC5776ff'
                  }}
                ></div>
              )}
            </div>
          )}
        </td>
      )}

      <td className="text-no-wrap">
        <RateCell
          id={`costRate-${user.id}`}
          ariaLabel={`Cost rate for ${user.firstname} ${user.lastname}`}
          savedRate={user.project_cost_rate}
          fallbackRate={defaultCostRate?.cost_rate ?? null}
          onSave={(value) => onSaveRate({ userId: user.id, field: 'cost_rate', value })}
          renderHint={renderCostHint}
        />
      </td>

      {chargePerMember && (
        <td className="text-no-wrap">
          <RateCell
            id={`billableRate-${user.id}`}
            ariaLabel={`Billable rate for ${user.firstname} ${user.lastname}`}
            savedRate={user.project_billable_rate}
            onSave={(value) => onSaveRate({ userId: user.id, field: 'billable_rate', value })}
          />
        </td>
      )}

      <td>
        <div className="dropdown" data-bs-toggle="dropdown" aria-expanded="false">
          <button type="button" className="btn btn-secondary">
            <DotsThree size={18} color={COLOR_CONTRACT_BLACK} weight="bold" />
          </button>
          <ul className="dropdown-menu">
            <li>
              <Link className="dropdown-item" to={PROFILE_DETAILS_ROUTER({ profileId: user.id })}>
                View profile
              </Link>
            </li>
            <li>
              <button type="button" className="dropdown-item" onClick={() => setVisibleBudgetModal(true)}>
                Manage budget
              </button>
            </li>
            {PERMISSION_BY_ROLE.PROJECT.TEAM_SECTION.DELETE_USER.includes(userContext.role) && (
              <>
                <li>
                  <hr className="dropdownDivider" />
                </li>
                <li>
                  <button
                    type="button"
                    className="dropdown-item text-danger"
                    onClick={() => onDelete({ userId: user.id })}>
                    Remove team member
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </td>
    </tr>
  )
}

export default TableUserTeamRow
